import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.#ensureIframeTitles();
    this.#ensureLinkNames();
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Adds an accessible name to YouTube/Vimeo iframes
   */
  #ensureIframeTitles() {
    const fallback =
      this.getAttribute('data-embedded-video-label')?.trim() || 'Embedded video';

    this.querySelectorAll('iframe').forEach((iframe) => {
      if (!(iframe instanceof HTMLIFrameElement)) return;
      if (iframe.title?.trim()) return;

      const src = iframe.getAttribute('src') || '';
      const isVideoEmbed =
        /youtube\.com|youtu\.be|player\.vimeo\.com|vimeo\.com/i.test(src);
      if (!isVideoEmbed) return;

      const heading = this.#nearestHeadingText(iframe);
      iframe.title = heading || fallback;
    });
  }

  /**
   * Gives empty/nameless links an accessible name
   */
  #ensureLinkNames() {
    this.querySelectorAll('a[href]').forEach((anchor) => {
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (this.#hasAccessibleName(anchor)) return;

      const label = this.#labelFromHref(anchor.href);
      if (label) {
        anchor.setAttribute('aria-label', label);
      }
    });
  }

  /**
   * @param {Element} el
   * @returns {string}
   */
  #nearestHeadingText(el) {
    let node = el.previousElementSibling;
    while (node) {
      if (/^H[1-6]$/i.test(node.tagName) && node.textContent?.trim()) {
        return node.textContent.trim();
      }
      node = node.previousElementSibling;
    }
    return '';
  }

  /**
   * @param {HTMLElement} el
   * @returns {boolean}
   */
  #hasAccessibleName(el) {
    if (el.getAttribute('aria-label')?.trim()) return true;
    if (el.getAttribute('aria-labelledby')?.trim()) return true;
    if (el.getAttribute('title')?.trim()) return true;
    if (el.textContent?.trim()) return true;

    const img = el.querySelector('img[alt]');
    if (img instanceof HTMLImageElement && img.alt.trim()) return true;

    const labelled = el.querySelector('[aria-label], .visually-hidden');
    if (
      labelled?.getAttribute('aria-label')?.trim() ||
      labelled?.textContent?.trim()
    ) {
      return true;
    }

    return false;
  }

  /**
   * @param {string} href
   * @returns {string}
   */
  #labelFromHref(href) {
    try {
      const url = new URL(href, window.location.origin);
      const segment = url.pathname.split('/').filter(Boolean).pop();
      if (!segment) return url.hostname || href;
      return segment.replace(/[-_]+/g, ' ');
    } catch {
      return href;
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
